/**
 * LE NOM HUMAIN DES DRAPEAUX D'UN ARTICLE — écrit UNE fois, lu partout.
 *
 * POURQUOI CE MODULE EXISTE (demande utilisateur 2026-09-05, avant de lancer une reprise : « j'aimerais
 * que les flags pour le non stock et non stats soient parfaitement compréhensibles pour un humain, je
 * ne veux pas des noms de colonnes difficiles à identifier »).
 *
 * ⚠️ LE DÉFAUT MESURÉ : les deux mêmes colonnes portaient QUATRE libellés, dont deux de polarité
 * OPPOSÉE. La fiche affichait « Non géré en stock », l'historique « Géré en stock », les inventaires
 * « Hors suivi de stock ». Un gérant qui cochait « Non géré en stock » puis ouvrait l'historique y
 * lisait « Géré en stock : oui → non » — la même action, décrite à l'envers, deux écrans plus loin.
 * Aucun de ces libellés n'est faux isolément ; c'est leur coexistence qui rend le réglage illisible.
 *
 * ⚠️ UNE SEULE PHRASE, UNE SEULE POLARITÉ. Le sens affiché est celui du GESTE que l'opérateur pose
 * (« je sors cet article de… »), jamais celui de la colonne : `is_stock_managed` est stocké en
 * positif, l'écran en montre l'inverse, et c'est `inverse` qui porte cette bascule — en un seul
 * endroit, plutôt que dans chaque écran qui pense à la faire.
 *
 * ⚠️ ÉCART ASSUMÉ AVEC LA MAQUETTE, et elle se contredit elle-même : sa fiche dit « Non géré en
 * stock », ses inventaires « sortir du suivi de stock ». On retient « suivi », plus concret pour un
 * commerçant que « géré », et surtout on retient LE MÊME MOT aux deux endroits.
 */

/** Un drapeau tel qu'un humain le lit — pas tel que la colonne le stocke. */
import { key, type Translatable } from './i18n';
import type { ItemKind } from '@orgazone/core-catalog';
export interface ArticleFlagLabel {
  /** La phrase, à l'affirmative du GESTE : cocher, c'est exclure. */
  label: Translatable;
  /** Ce que le réglage change vraiment — la seule chose qu'un gérant risque de comprendre de travers. */
  help: Translatable;
  /**
   * La colonne stocke-t-elle l'INVERSE de ce que la phrase affirme ?
   * ⚠️ `is_stock_managed` vaut 1 quand l'article EST suivi ; la phrase dit « hors suivi ». Sans cette
   * bascule déclarée ici, chaque écran la referait de son côté — et l'un d'eux l'oublierait.
   */
  inverse?: true;
}

/**
 * Les deux drapeaux d'exclusion, par NOM DE COLONNE (`snake_case`, comme la base et l'historique).
 * ⚠️ La clé est la colonne parce que l'historique ne connaît que ça : lui donner un dictionnaire
 * indexé sur les noms camelCase de l'API l'obligerait à traduire deux fois.
 */
export const ARTICLE_FLAG_LABELS: Record<string, ArticleFlagLabel> = {
  excluded_from_stats: {
    label: key('Exclu des statistiques de vente'),
    help: key('Reste dans le chiffre d’affaires et dans le fiscal, mais sort des analyses par article, famille et vendeur.'),
  },
  is_stock_managed: {
    label: key('Hors suivi de stock'),
    help: key('Vendable sans décrémenter le stock ; absent des inventaires et des écarts. C’est le réglage des chèques cadeaux, des retouches et des articles de service.'),
    inverse: true,
  },
};

/** Le nom camelCase de l'API → la colonne, pour que la fiche lise le même dictionnaire. */
export const ARTICLE_FLAG_COLUMN: Record<string, string> = {
  excludedFromStats: 'excluded_from_stats',
  isStockManaged: 'is_stock_managed',
};

/** La phrase d'un drapeau, par nom de colonne — `null` si ce champ n'en est pas un. */
export function articleFlagLabel(column: string): ArticleFlagLabel | null {
  return ARTICLE_FLAG_LABELS[column] ?? null;
}

/**
 * La valeur d'un drapeau telle qu'elle se lit SOUS SA PHRASE.
 * ⚠️ C'est ici, et nulle part ailleurs, que l'inversion se fait. Un écran qui rendrait la valeur brute
 * sous une phrase inversée afficherait le contraire de la vérité — c'était le cas de l'historique.
 */
export function articleFlagReads(column: string, stored: boolean): boolean {
  return ARTICLE_FLAG_LABELS[column]?.inverse ? !stored : stored;
}

/**
 * LA NATURE D'UN ARTICLE, telle qu'un humain la lit — la fiche, la création et l'historique la nomment ici.
 * ⚠️ INDEXÉE PAR LA LISTE DU CŒUR (`core-catalog` ITEM_KINDS) : une nature ajoutée réclame son texte à la compilation.
 * ⚠️ « Chèque cadeau repris » ET NON « Titre » : la clé « Titre » existe déjà au sens d'un INTITULÉ, et le français étant
 *    la clé, l'homonyme hériterait de sa traduction (« Heading »). Et la nature ne se choisit pas : elle ne désigne que
 *    l'article par lequel l'ancien logiciel vendait ses chèques cadeaux, que la reprise marque et archive.
 * ⚠️ AUCUNE PROMESSE SUR LA CAISSE : la fenêtre Retouche ne liste les prestations qu'avec l'APK qui les lit.
 */
export const ITEM_KIND_TEXT: Record<ItemKind, { label: Translatable; help: Translatable }> = {
  goods: { label: key('Marchandise'), help: key('Un article qu’on achète, qu’on stocke et qu’on vend.') },
  service: { label: key('Prestation'), help: key('Une retouche, un service : ni stock, ni coût d’achat — sa marge est son prix hors taxe.') },
  title: {
    label: key('Chèque cadeau repris'),
    help: key('L’article par lequel l’ancien logiciel vendait ses chèques cadeaux. Ses ventes ne sont pas du chiffre, puisqu’un chèque cadeau sera dépensé sur des articles ; la reprise le marque et l’archive. En caisse, un chèque cadeau se vend par son bouton, jamais comme un article.'),
  },
};
